/**
 * Save state machine for the editor surface (spec.md § Editor / Save state
 * machine, § UI & design / Save state machine).
 *
 * States:
 * - `clean`    — content matches the last saved server version; nothing to save.
 * - `unsaved`  — local content has diverged from the server; a save is pending
 *                the user's action.
 * - `saving`   — a PUT is in flight. The editor stays editable; keystrokes that
 *                land mid-flight are tracked so we can decide the post-save
 *                state.
 * - `saved`    — the last save succeeded and local content has not moved since.
 * - `conflict` — the server returned 409; a newer version exists. The inline
 *                banner is shown until the user reloads theirs.
 *
 * This module is a pure reducer with no React or network dependencies so the
 * full transition cycle is unit-testable in isolation (see saveState.test.ts).
 */

export type SaveState = "clean" | "unsaved" | "saving" | "saved" | "conflict";

export type SaveEvent =
  /** Local content (title or body) changed away from the saved baseline. */
  | { type: "EDIT" }
  /** User (or Cmd/Ctrl+S) requested a save. */
  | { type: "SAVE" }
  /**
   * A save succeeded.
   * `dirtySinceRequest` is true when the user typed while the PUT was in
   * flight — per spec, the editor returns to `unsaved` (not `saved`) in that
   * case so the newer keystrokes are not silently considered persisted.
   */
  | { type: "SAVE_SUCCESS"; dirtySinceRequest: boolean }
  /** A non-409 save failure (network / 5xx). Returns to `unsaved` to retry. */
  | { type: "SAVE_ERROR" }
  /** The server returned 409 Conflict. */
  | { type: "SAVE_CONFLICT" }
  /** The conflict was resolved (user reloaded the server version). */
  | { type: "CONFLICT_RESOLVED" };

/**
 * Pure transition function. Unhandled (state, event) pairs are no-ops that
 * return the current state — this keeps the machine forgiving of duplicate or
 * out-of-order events (e.g. an EDIT arriving during `saving`).
 */
export function saveStateReducer(state: SaveState, event: SaveEvent): SaveState {
  switch (event.type) {
    case "EDIT":
      // Editing always marks the doc dirty unless a save is in flight, in which
      // case the in-flight tracking (SAVE_SUCCESS.dirtySinceRequest) decides
      // the resulting state. A conflict stays a conflict until resolved.
      if (state === "saving" || state === "conflict") return state;
      return "unsaved";

    case "SAVE":
      // Fire immediately from any savable state. A second SAVE while already
      // saving is ignored (returns `saving`).
      if (state === "conflict") return state;
      return "saving";

    case "SAVE_SUCCESS":
      return event.dirtySinceRequest ? "unsaved" : "saved";

    case "SAVE_ERROR":
      // Save stays in `unsaved` so the existing Save button is the retry
      // (spec: "No retry button — the existing Save is the retry").
      return "unsaved";

    case "SAVE_CONFLICT":
      return "conflict";

    case "CONFLICT_RESOLVED":
      // After reloading theirs, the editor content equals the server version.
      return "clean";

    default:
      return state;
  }
}

/** Whether the current state means there is unsaved local content. */
export function hasUnsavedChanges(state: SaveState): boolean {
  return state === "unsaved" || state === "conflict";
}
